// Two-way stats sync between local storage and Supabase.
//
// Strategy:
//   - On startup: fetch remote stats; merge with local (take the row that
//     reflects the most progress — higher cumulative_xp wins).
//   - On local stats change: debounce, then upload.
//   - Network failures are tolerated; the game keeps working offline.

import type { SupabaseClient } from "@supabase/supabase-js";
import { getSupabase } from "./supabase.ts";
import {
  DEFAULT_DETAILED_STATS,
  loadStats,
  saveStats,
  type Stats,
} from "../storage/local.ts";

const STATS_TABLE = "stats";

interface RemoteStatsRow {
  user_id: string;
  cumulative_xp: number;
  games_played: number;
  games_won: number;
  best_time_sec_one: number | null;
  best_time_sec_three: number | null;
  fewest_moves_one: number | null;
  fewest_moves_three: number | null;
  longest_streak_days: number;
  current_streak_days: number;
  last_win_date_local: string | null;
  first_win_date_of_today_local: string | null;
  total_undos: number;
  total_hints: number;
}

function statsToRow(userId: string, s: Stats): RemoteStatsRow {
  return {
    user_id: userId,
    cumulative_xp: s.cumulativeXp,
    games_played: s.gamesPlayed,
    games_won: s.gamesWon,
    best_time_sec_one: s.bestTimeSecOne,
    best_time_sec_three: s.bestTimeSecThree,
    fewest_moves_one: s.fewestMovesOne,
    fewest_moves_three: s.fewestMovesThree,
    longest_streak_days: s.longestStreakDays,
    current_streak_days: s.currentStreakDays,
    last_win_date_local: s.lastWinDateLocal,
    first_win_date_of_today_local: s.firstWinDateOfTodayLocal,
    total_undos: s.totalUndos,
    total_hints: s.totalHints,
  };
}

function rowToStats(row: RemoteStatsRow): Stats {
  return {
    cumulativeXp: row.cumulative_xp,
    gamesPlayed: row.games_played,
    gamesWon: row.games_won,
    bestTimeSecOne: row.best_time_sec_one,
    bestTimeSecThree: row.best_time_sec_three,
    fewestMovesOne: row.fewest_moves_one,
    fewestMovesThree: row.fewest_moves_three,
    longestStreakDays: row.longest_streak_days,
    currentStreakDays: row.current_streak_days,
    lastWinDateLocal: row.last_win_date_local,
    firstWinDateOfTodayLocal: row.first_win_date_of_today_local,
    totalUndos: row.total_undos,
    totalHints: row.total_hints,
    jokerCards: 0,
    jokerCardsEarned: 0,
    jokerCardsSpent: 0,
    detailed: {
      one: { ...DEFAULT_DETAILED_STATS.one },
      three: { ...DEFAULT_DETAILED_STATS.three },
    },
  };
}

// Pick the "more advanced" stats — the one with higher XP wins overall, but
// best-time / fewest-moves merge by min, and counters by max.
export function mergeStats(local: Stats, remote: Stats): Stats {
  const minOrNull = (a: number | null, b: number | null) =>
    a == null ? b : b == null ? a : Math.min(a, b);
  const advancedByXp = remote.cumulativeXp >= local.cumulativeXp ? remote : local;
  return {
    cumulativeXp: Math.max(local.cumulativeXp, remote.cumulativeXp),
    gamesPlayed: Math.max(local.gamesPlayed, remote.gamesPlayed),
    gamesWon: Math.max(local.gamesWon, remote.gamesWon),
    bestTimeSecOne: minOrNull(local.bestTimeSecOne, remote.bestTimeSecOne),
    bestTimeSecThree: minOrNull(local.bestTimeSecThree, remote.bestTimeSecThree),
    fewestMovesOne: minOrNull(local.fewestMovesOne, remote.fewestMovesOne),
    fewestMovesThree: minOrNull(local.fewestMovesThree, remote.fewestMovesThree),
    longestStreakDays: Math.max(local.longestStreakDays, remote.longestStreakDays),
    // Streak / daily fields follow the more-XP record (treat as "current truth").
    currentStreakDays: advancedByXp.currentStreakDays,
    lastWinDateLocal: advancedByXp.lastWinDateLocal,
    firstWinDateOfTodayLocal: advancedByXp.firstWinDateOfTodayLocal,
    totalUndos: Math.max(local.totalUndos, remote.totalUndos),
    totalHints: Math.max(local.totalHints, remote.totalHints),
    // Joker inventory is currently local-only because it is a consumable.
    // Avoid resurrecting or deleting cards during the XP-based stats merge.
    jokerCards: local.jokerCards,
    jokerCardsEarned: local.jokerCardsEarned,
    jokerCardsSpent: local.jokerCardsSpent,
    detailed: local.detailed,
  };
}

export interface SyncContext {
  userId: string;
  client: SupabaseClient;
}

let syncContext: SyncContext | null = null;
let uploadTimer: number | null = null;

// Pull stats from server, merge with local, persist locally. Returns the merged stats.
export async function pullAndMergeStats(ctx: SyncContext): Promise<Stats> {
  const local = loadStats();
  const { data, error } = await ctx.client
    .from(STATS_TABLE)
    .select("*")
    .eq("user_id", ctx.userId)
    .maybeSingle();
  if (error) {
    console.warn("[sync] pull failed:", error.message);
    return local;
  }
  if (!data) {
    // No remote row yet — push local as the seed.
    await pushStats(ctx, local);
    return local;
  }
  const merged = mergeStats(local, rowToStats(data as RemoteStatsRow));
  saveStats(merged);
  return merged;
}

export async function pushStats(ctx: SyncContext, s: Stats): Promise<void> {
  const { error } = await ctx.client
    .from(STATS_TABLE)
    .upsert(statsToRow(ctx.userId, s), { onConflict: "user_id" });
  if (error) console.warn("[sync] push failed:", error.message);
}

// Schedule a debounced upload of the current local stats.
export function scheduleStatsUpload(s: Stats, delayMs = 1500): void {
  if (!syncContext) return;
  if (uploadTimer != null) window.clearTimeout(uploadTimer);
  uploadTimer = window.setTimeout(() => {
    uploadTimer = null;
    if (syncContext) void pushStats(syncContext, s);
  }, delayMs);
}

export function setSyncContext(ctx: SyncContext | null): void {
  syncContext = ctx;
}

export function getSyncContext(): SyncContext | null {
  return syncContext;
}

// Initialize sync after auth: pull, merge, and arm uploads.
export async function initStatsSync(): Promise<Stats | null> {
  const sb = getSupabase();
  if (!sb) return null;
  const { data } = await sb.auth.getSession();
  if (!data.session) return null;
  const ctx: SyncContext = { client: sb, userId: data.session.user.id };
  setSyncContext(ctx);
  return pullAndMergeStats(ctx);
}
